/**
 * Stadium banner shown between Ribbon and the field grid. Generic stadium
 * SVG + venue name + city/state. Hover/focus opens a Tooltip with weather,
 * local time + date, and attendance (#/# + occupancy %).
 *
 * Per-stadium iconic imagery is out of scope — see TODO.md.
 */
import { useCallback, useState } from 'react';
import type { VenueInfo } from '../state/selectors';
import Tooltip from './Tooltip';
import styles from './VenueBadge.module.css';

interface VenueBadgeProps {
  venue: VenueInfo;
}

function formatNumber(n: number): string {
  return n.toLocaleString('en-US');
}

function formatLocalTime(iso: string, tz: string | null): string | null {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const opts: Intl.DateTimeFormatOptions = {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: tz ?? undefined,
      timeZoneName: tz ? 'short' : undefined,
    };
    return new Intl.DateTimeFormat('en-US', opts).format(d);
  } catch {
    return null;
  }
}

function StadiumIcon({ size = 22 }: { size?: number }): JSX.Element {
  return (
    <svg
      viewBox="0 0 32 24"
      width={size}
      height={(size * 24) / 32}
      aria-hidden="true"
      focusable="false"
      className={styles.icon}
    >
      <path
        d="M16 4 C 6 4, 1 9, 1 14 L 31 14 C 31 9, 26 4, 16 4 Z"
        fill="currentColor"
        opacity="0.18"
      />
      <path
        d="M2 14 L 30 14 L 30 17 L 2 17 Z"
        fill="currentColor"
        opacity="0.4"
      />
      <path
        d="M3 17 L 29 17 L 27 22 L 5 22 Z"
        fill="currentColor"
        opacity="0.7"
      />
      <line x1="8" y1="17" x2="9" y2="22" stroke="currentColor" strokeWidth="0.6" opacity="0.5" />
      <line x1="16" y1="17" x2="16" y2="22" stroke="currentColor" strokeWidth="0.6" opacity="0.5" />
      <line x1="24" y1="17" x2="23" y2="22" stroke="currentColor" strokeWidth="0.6" opacity="0.5" />
    </svg>
  );
}

export default function VenueBadge({ venue }: VenueBadgeProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);

  const onEnter = useCallback((e: React.SyntheticEvent<HTMLButtonElement>) => {
    setAnchorRect(e.currentTarget.getBoundingClientRect());
    setOpen(true);
  }, []);
  const onLeave = useCallback(() => setOpen(false), []);
  const onClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorRect(e.currentTarget.getBoundingClientRect());
      setOpen((v) => !v);
    },
    [],
  );

  const subtitle = [venue.city, venue.stateAbbrev].filter(Boolean).join(', ');

  const occupancyPct =
    venue.occupancy != null ? Math.round(venue.occupancy * 100) : null;
  const attendanceLine =
    venue.attendance != null && venue.capacity != null
      ? `${formatNumber(venue.attendance)} / ${formatNumber(venue.capacity)}${occupancyPct != null ? ` (${occupancyPct}%)` : ''}`
      : null;

  const localTime = venue.firstPitchIso
    ? formatLocalTime(venue.firstPitchIso, venue.timezoneId)
    : null;

  return (
    <div className={styles.wrap} data-testid="venue-badge">
      <button
        type="button"
        className={styles.trigger}
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
        onFocus={onEnter}
        onBlur={onLeave}
        onClick={onClick}
        aria-label={`${venue.name} details`}
      >
        <StadiumIcon size={22} />
        <span className={styles.text}>
          <span className={styles.name}>{venue.name}</span>
          {subtitle && <span className={styles.subtitle}>{subtitle}</span>}
        </span>
      </button>
      <Tooltip
        open={open}
        anchorRect={anchorRect}
        onRequestClose={() => setOpen(false)}
        width={260}
      >
        <div className={styles.card} data-testid="venue-tooltip-card">
          <div className={styles.cardHeader}>
            <div className={styles.cardName}>{venue.name}</div>
            {subtitle && <div className={styles.cardSubtitle}>{subtitle}</div>}
          </div>
          {localTime && (
            <div className={styles.row} data-testid="venue-time">
              <span className={styles.rowLabel}>First pitch</span>
              <span className={styles.rowValue}>{localTime}</span>
            </div>
          )}
          {venue.weather && (
            <div className={styles.row} data-testid="venue-weather">
              <span className={styles.rowLabel}>Weather</span>
              <span className={styles.rowValue}>
                {[venue.weather.condition, venue.weather.temp && `${venue.weather.temp}°F`]
                  .filter(Boolean)
                  .join(' · ')}
                {venue.weather.wind && (
                  <span className={styles.windLine}>Wind {venue.weather.wind}</span>
                )}
              </span>
            </div>
          )}
          {attendanceLine && (
            <div className={styles.attendance} data-testid="venue-attendance">
              <div className={styles.attendanceLine}>
                <span className={styles.rowLabel}>Attendance</span>
                <span className={styles.attendanceValue}>{attendanceLine}</span>
              </div>
              {occupancyPct != null && (
                <div
                  className={styles.occupancyBar}
                  role="progressbar"
                  aria-valuenow={occupancyPct}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label="Attendance vs capacity"
                >
                  <div
                    className={styles.occupancyFill}
                    style={{ width: `${Math.min(100, Math.max(0, occupancyPct))}%` }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </Tooltip>
    </div>
  );
}
